// src/native/compliance/MinorsAffirmation.jsx
//
// WS-7 COMPLIANCE. App Store Guideline 5.1.4(b) and GDPR Article 8.
//
// 5.1.4(b) binds any app that collects an email address, has the ability to
// chat, or captures audio from a minor. Timo does all three, with children who
// have a speech delay, so this exists regardless of App Store category. It is
// not the Kids Category, which this stack cannot satisfy and which is sticky
// once selected.
//
// Shape, and why:
//   - This is NOT a separate step. It is a sentence and a quiet escape hatch
//     directly above the single "I agree" button, so the common case stays one
//     tap. The gate now runs on the web too, inside a funnel with real revenue
//     in it, and a two screen wall would cost conversion without adding any
//     legal weight. Tapping "I agree" is the explicit affirmative act, and the
//     sentence above it is what that act affirms.
//   - It never asks for a date of birth. A single affirmation answers the legal
//     question and collects nothing, which is the right trade on a screen whose
//     whole subject is data minimisation.
//   - A child who says they are under the threshold is not locked out and is
//     not told off. They are asked to fetch a grown up, in one short sentence.
//
// The three ages in the copy:
//   13  COPPA, United States
//   14  GDPR Article 8 as implemented in Lithuania, the primary market
//   16  GDPR Article 8 default, most other member states

import React from 'react';
import { cn } from '@/lib/utils';

export default function MinorsAffirmation({ t, lightMode, underAge, onDeclareUnderAge }) {
  return (
    <div className="flex flex-col gap-2">
      <p
        className={cn(
          'text-[13.5px] leading-relaxed',
          lightMode ? 'text-gray-700' : 'text-white/75'
        )}
      >
        {t('native.minors.affirmation')}
      </p>

      <p
        className={cn(
          'text-[12.5px] leading-relaxed',
          lightMode ? 'text-gray-500' : 'text-white/50'
        )}
      >
        {t('native.minors.ageNote')}
      </p>

      {underAge ? (
        <div
          role="status"
          className={cn(
            'mt-1 rounded-2xl border p-3.5 text-[13.5px] leading-relaxed',
            lightMode
              ? 'border-[#E9C6B4] bg-[#FDF3EE] text-gray-800'
              : 'border-[#E07A4D]/40 bg-[#E07A4D]/10 text-white/85'
          )}
        >
          {t('native.minors.askGrownUp')}
        </div>
      ) : (
        <button
          type="button"
          onClick={onDeclareUnderAge}
          className={cn(
            'self-start min-h-[44px] text-[13px] font-medium underline underline-offset-2 touch-manipulation',
            lightMode ? 'text-gray-500' : 'text-white/50'
          )}
        >
          {t('native.minors.underAgeLink')}
        </button>
      )}
    </div>
  );
}
